import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight, Lock, Check } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { checkOwner, signInWithGoogle, getSavedPin, savePin, clearPin } from "../lib/auth";
import { scrollToId } from "../lib/scroll";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const dateKey = (y, m, d) =>
  `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

export default function Schedule() {
  const [blocked, setBlocked] = useState({});
  const [cursor, setCursor] = useState(() => {
    const now = new Date();
    return { y: now.getFullYear(), m: now.getMonth() };
  });
  const [managing, setManaging] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [authMethod, setAuthMethod] = useState(null);
  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState(false);
  const [busy, setBusy] = useState(false);
  const today = new Date().toLocaleDateString("en-CA");
  // Multi-day picking: tournaments often span several days. Tap to add,
  // tap again to remove; the button below carries them to the booking form.
  const [pickedDates, setPickedDates] = useState([]);
  const ownerMode = new URLSearchParams(window.location.search).has("manage");

  const fetchSchedule = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/schedule`);
      const map = {};
      data.forEach((b) => {
        map[b.date] = b.id;
      });
      setBlocked(map);
    } catch {
      setBlocked({});
    }
  }, []);

  useEffect(() => {
    fetchSchedule();
  }, [fetchSchedule]);

  useEffect(() => {
    if (!ownerMode) return;
    let cancelled = false;
    (async () => {
      const user = await checkOwner();
      if (cancelled) return;
      if (user) {
        setAuthMethod("google");
        setAuthed(true);
        setManaging(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ownerMode]);

  const checkPin = async (e) => {
    e.preventDefault();
    const entered = pin.replace(/\s/g, "").trim();
    if (!entered) {
      setPinError(true);
      return;
    }
    setBusy(true);
    try {
      await axios.get(`${API}/schedule/pin-check`, {
        headers: { "x-schedule-pin": entered },
      });
      savePin(entered);
      setPin(entered);
      setAuthMethod("pin");
      setAuthed(true);
      setPinError(false);
    } catch {
      setPinError(true);
    }
    setBusy(false);
  };

  const toggleDay = async (key) => {
    if (busy) return;
    setBusy(true);
    try {
      const headers = authMethod === "pin" ? { "x-schedule-pin": pin } : {};
      if (blocked[key]) {
        await axios.delete(`${API}/schedule/${blocked[key]}`, { headers });
      } else {
        await axios.post(`${API}/schedule`, { date: key }, { headers });
      }
      await fetchSchedule();
    } catch (err) {
      if (err.response?.status === 401) {
        setAuthed(false);
        setAuthMethod(null);
        setPin("");
        setPinError(true);
        clearPin();
      }
    }
    setBusy(false);
  };

  const moveMonth = (dir) =>
    setCursor(({ y, m }) => {
      const nm = m + dir;
      return { y: y + Math.floor(nm / 12), m: ((nm % 12) + 12) % 12 };
    });

  const cells = useMemo(() => {
    const first = new Date(cursor.y, cursor.m, 1).getDay();
    const total = new Date(cursor.y, cursor.m + 1, 0).getDate();
    return [...Array(first).fill(null), ...Array.from({ length: total }, (_, i) => i + 1)];
  }, [cursor]);

  const monthLabel = new Date(cursor.y, cursor.m, 1).toLocaleString("en-US", {
    month: "long",
    year: "numeric",
  });

  const fmtShort = (key) =>
    new Date(`${key}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });

  const dayClass = (key, isBlocked, isPast, isPicked) => {
    if (isPicked)
      return "bg-gradient-to-br from-accent/30 to-accentlight/20 border-2 border-accentlight text-white font-bold shadow-[0_0_18px_rgba(79,195,247,0.35)]";
    if (isBlocked)
      return "bg-gradient-to-br from-red-500/25 to-red-500/10 border border-red-400/40 text-white/45 line-through";
    if (isPast) return "border border-transparent text-white/20";
    if (managing && authed)
      return "border border-white/10 text-white/80 hover:border-accentlight/70 hover:text-accentlight transition-colors duration-200 cursor-pointer";
    return "border border-white/10 text-white/80 hover:border-accentlight hover:bg-accentlight/10 hover:text-white transition-all duration-200 cursor-pointer";
  };

  const pickedLabel = () => {
    const open = pickedDates.filter((k) => !blocked[k]);
    const list = open.slice(0, 3).map(fmtShort).join(", ") + (open.length > 3 ? ` +${open.length - 3} more` : "");
    return `Book ${open.length} day${open.length === 1 ? "" : "s"} — ${list}`;
  };

  return (
    <section id="schedule" className="relative bg-surface/40 py-24 md:py-36">
      <div className="mx-auto grid w-full max-w-7xl grid-cols-1 gap-14 px-6 md:px-10 lg:grid-cols-2 lg:gap-20">
        <div>
          <SectionHeading
            eyebrow="Availability"
            title={
              <>
                GAME DAY
                <br />
                <span className="bg-gradient-to-r from-accent to-accentlight bg-clip-text text-transparent">
                  SCHEDULE
                </span>
              </>
            }
          />
          <Reveal delay={0.15}>
            <p className="mt-8 max-w-md text-base leading-relaxed text-white/70">
              Check whether your game day is still open. Crossed-out dates are
              already booked — everything else is up for grabs. Tap one day, or
              several for tournaments, then continue to the booking form.
            </p>
          </Reveal>

          <Reveal delay={0.25}>
            <div className="mt-8 flex items-center gap-6 text-xs font-semibold uppercase tracking-[0.2em] text-white/60">
              <span className="flex items-center gap-2">
                <span className="inline-block h-3 w-3 rounded-sm border border-white/20 bg-transparent" />
                Open
              </span>
              <span className="flex items-center gap-2">
                <span className="inline-block h-3 w-3 rounded-sm bg-red-500/25 border border-red-400/40" />
                Booked
              </span>
            </div>
          </Reveal>

          {ownerMode && (
          <Reveal delay={0.35}>
            <div className="mt-10">
              {!managing ? (
                <button
                  onClick={async () => {
                    setManaging(true);
                    setPinError(false);
                    const user = await checkOwner();
                    if (user) {
                      setAuthMethod("google");
                      setAuthed(true);
                      return;
                    }
                    // A remembered PIN unlocks manage mode without retyping it.
                    const saved = getSavedPin();
                    if (saved) {
                      try {
                        await axios.get(`${API}/schedule/pin-check`, { headers: { "x-schedule-pin": saved } });
                        setPin(saved);
                        setAuthMethod("pin");
                        setAuthed(true);
                      } catch {
                        clearPin();
                      }
                    }
                  }}
                  data-testid="schedule-manage-button"
                  className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-white/35 transition-colors duration-200 hover:text-accentlight"
                >
                  <Lock className="h-3.5 w-3.5" /> Manage schedule
                </button>
              ) : !authed ? (
                <div className="flex flex-col items-start gap-3">
                  <button
                    type="button"
                    onClick={() => signInWithGoogle("/?manage")}
                    data-testid="schedule-google-button"
                    className="inline-flex items-center gap-3 rounded-full bg-white px-6 py-3 text-sm font-bold text-[#1f1f1f] transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_10px_30px_-10px_rgba(255,255,255,0.4)]"
                  >
                    <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
                      <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.1h6.5c-.1 1.1-.8 2.7-2.4 3.8l3.7 2.9c2.2-2 3.7-5 3.7-8.6z" />
                      <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.8-2.9l-3.7-2.9c-1 .7-2.4 1.2-4.1 1.2-3.1 0-5.8-2.1-6.8-5H1.4v3C3.3 21.3 7.3 24 12 24z" />
                      <path fill="#FBBC05" d="M5.2 14.4c-.2-.7-.4-1.5-.4-2.4s.1-1.7.4-2.4v-3H1.4C.5 8.2 0 10 0 12s.5 3.8 1.4 5.4l3.8-3z" />
                      <path fill="#EA4335" d="M12 4.8c1.8 0 3 .8 3.7 1.4l3.3-3.2C17.9 1.1 15.2 0 12 0 7.3 0 3.3 2.7 1.4 6.6l3.8 3c1-2.9 3.7-4.8 6.8-4.8z" />
                    </svg>
                    Sign in with Google
                  </button>
                  <form onSubmit={checkPin} className="flex items-center gap-3" data-testid="schedule-pin-form">
                  <input
                    type="password"
                    inputMode="numeric"
                    autoComplete="off"
                    value={pin}
                    onChange={(e) => setPin(e.target.value)}
                    placeholder="Your PIN"
                    data-testid="schedule-pin-input"
                    className="w-40 rounded-lg border border-white/10 bg-surface px-4 py-2.5 text-sm text-white placeholder:text-white/30 outline-none focus:border-accentlight"
                  />
                  <button
                    type="submit"
                    disabled={busy}
                    data-testid="schedule-pin-submit-button"
                    className="rounded-full bg-gradient-to-r from-accent to-accentlight px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-ink transition-all hover:brightness-110 disabled:opacity-60"
                  >
                    Unlock
                  </button>
                  {pinError && (
                    <p data-testid="schedule-pin-error" className="text-xs text-red-300">
                      Wrong PIN
                    </p>
                  )}
                </form>
                  <button
                    type="button"
                    onClick={() => {
                      setManaging(false);
                      setPin("");
                      setPinError(false);
                    }}
                    data-testid="schedule-cancel-button"
                    className="text-xs font-semibold uppercase tracking-wider text-white/40 transition-colors hover:text-white"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-4">
                  <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-accentlight" data-testid="schedule-editing-hint">
                    <Lock className="h-3.5 w-3.5" /> Tap a date to block / unblock
                  </p>
                  <button
                    onClick={() => {
                      setManaging(false);
                      setAuthed(false);
                      setAuthMethod(null);
                      setPin("");
                    }}
                    data-testid="schedule-done-button"
                    className="inline-flex items-center gap-2 rounded-full border border-accentlight/40 px-4 py-2 text-xs font-bold uppercase tracking-wider text-accentlight transition-colors hover:bg-accentlight/10"
                  >
                    <Check className="h-3.5 w-3.5" /> Done
                  </button>
                </div>
              )}
            </div>
          </Reveal>
          )}
        </div>

        <Reveal delay={0.2}>
          <div className="rounded-2xl border border-white/5 bg-ink p-6 md:p-8" data-testid="schedule-calendar">
            <div className="mb-6 flex items-center justify-between">
              <h3 className="font-display text-3xl tracking-wider" data-testid="schedule-month-label">
                {monthLabel}
              </h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => moveMonth(-1)}
                  data-testid="schedule-prev-month"
                  aria-label="Previous month"
                  className="rounded-lg border border-white/10 p-2 text-white/60 transition-colors hover:border-accentlight hover:text-accentlight"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  onClick={() => moveMonth(1)}
                  data-testid="schedule-next-month"
                  aria-label="Next month"
                  className="rounded-lg border border-white/10 p-2 text-white/60 transition-colors hover:border-accentlight hover:text-accentlight"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="mb-2 grid grid-cols-7 gap-1.5">
              {WEEKDAYS.map((w) => (
                <div key={w} className="py-1 text-center text-[10px] font-semibold uppercase tracking-[0.2em] text-white/35">
                  {w}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1.5">
              {cells.map((d, i) => {
                if (d === null) return <div key={`empty-${i}`} />;
                const key = dateKey(cursor.y, cursor.m, d);
                const isBlocked = Boolean(blocked[key]);
                const isPast = key < today;
                const isToday = key === today;
                const isPicked = pickedDates.includes(key);
                const manageMode = managing && authed;
                return (
                  <button
                    key={key}
                    type="button"
                    data-testid={`schedule-day-${key}`}
                    disabled={isPast || busy}
                    title={
                      isPast
                        ? undefined
                        : isBlocked
                        ? manageMode
                          ? "Tap to unblock this date"
                          : "Booked"
                        : manageMode
                        ? "Tap to block this date"
                        : isPicked
                        ? "Tap to unselect this day"
                        : "Tap to select this day — pick as many as you need"
                    }
                    onClick={() => {
                      if (manageMode) {
                        toggleDay(key);
                        return;
                      }
                      if (isBlocked || isPast) return;
                      setPickedDates((prev) =>
                        prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
                      );
                    }}
                    className={`relative flex aspect-square items-center justify-center rounded-lg text-sm ${dayClass(key, isBlocked, isPast, isPicked)} ${isToday && !isPicked ? "ring-1 ring-accentlight" : ""}`}
                  >
                    {d}
                  </button>
                );
              })}
            </div>

            {!managing && pickedDates.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  const dates = pickedDates.filter((k) => !blocked[k]);
                  setPickedDates([]);
                  if (!dates.length) return;
                  window.dispatchEvent(
                    new CustomEvent("gamelens:picked-date", { detail: dates.join(", ") })
                  );
                  scrollToId("#booking");
                }}
                data-testid="schedule-continue-button"
                className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-accent to-accentlight px-6 py-4 text-xs font-bold uppercase tracking-wider text-ink transition-all duration-300 hover:brightness-110"
              >
                {pickedLabel()}
              </button>
            )}

            <p className="mt-6 text-center text-xs text-white/40" data-testid="schedule-hint">
              {managing && authed
                ? "Blocked dates update live for every visitor."
                : "Tap any open date to select it — tap again to unselect. Pick one day or several, then continue to the booking form."}
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
